import { Component, signal, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AppService } from '@app/app.service';
import { Token } from '@app/auth/token.model';

@Component({
  selector: 'app-user-additional-email-add',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    MatTooltipModule,
    MatStepperModule,
    MatCheckboxModule,
  ],
  templateUrl: './user-additional-email-add.component.html',
  styleUrl: './user-additional-email-add.component.scss',
})
export class UserAdditionalEmailAddComponent implements OnDestroy {
  emailForm!: FormGroup;
  userForm!: FormGroup;

  step = signal(0);

  token: Token | null = null;

  resendEmailCodeTimerInitial = 30;
  resendEmailCodeInterval: any = null;
  resendEmailCodeTimer = signal(0);

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private router: Router
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      is_group_email: [false],
    });

    this.userForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      is_group_email: [false],
      token_code: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      token_uuid: ['', Validators.required],
    });
  }

  createToken(): void {
    if (this.emailForm.invalid) {
      this.appService.showSnackBar('Please enter a valid email address', 'error');
      return;
    }

    const isUniqueIdentifier = this.emailForm.value.is_group_email ? false : true;

    const data = {
      identifier: this.emailForm.value.email.toLowerCase(),
      unique_user_identifier: isUniqueIdentifier,
      delivery_method: 'email',
    };

    this.token = null;

    this.appService.post('create-token', data).subscribe({
      next: (response) => {
        this.token = response.data;

        this.step.set(1);
        this.startEmailCodeResendTimer();

        this.userForm.patchValue({
          email: this.emailForm.value.email.toLowerCase(),
          is_group_email: this.emailForm.value.is_group_email,
          token_uuid: this.token?.uuid,
        });
      },
      error: (error) => {
        this.appService.handleError(error);
      },
    });
  }

  addAdditionalEmail(): void {
    if (this.userForm.invalid) {
      this.appService.showSnackBar('Please enter a valid email and code', 'error');
      return;
    }

    const data = {
      email: this.userForm.value.email.toLowerCase(),
      is_group_email: this.userForm.value.is_group_email,
      token_code: this.userForm.value.token_code,
      email_verification_token_uuid: this.userForm.value.token_uuid,
    };

    this.appService.post('user/additional-emails', data).subscribe({
      next: (response) => {
        this.appService.showSnackBar(response.message, 'success');
        this.router.navigate(['/account']);
      },
      error: (error) => {
        this.appService.handleError(error);
      },
    });
  }

  startEmailCodeResendTimer(): void {
    this.resendEmailCodeTimer.set(this.resendEmailCodeTimerInitial);

    this.resendEmailCodeInterval = setInterval(() => {
      if (this.resendEmailCodeTimer() > 0) {
        this.resendEmailCodeTimer.set(this.resendEmailCodeTimer() - 1);
      } else {
        this.clearEmailCodeResendTimer();
      }
    }, 1000);
  }

  clearEmailCodeResendTimer(): void {
    if (this.resendEmailCodeInterval) {
      clearInterval(this.resendEmailCodeInterval);
      this.resendEmailCodeInterval = null;
    }
  }

  changeAdditionalEmail(): void {
    this.emailForm.reset();
    this.userForm.reset();

    this.token = null;
    this.step.set(0);

    this.clearEmailCodeResendTimer();
  }

  goBack(): void {
    this.router.navigate(['/account']);
  }

  ngOnDestroy(): void {
    this.clearEmailCodeResendTimer();
  }
}